import * as _ from 'lodash';
import * as moment from 'moment';
import { FormEvent, useEffect, useState } from 'react';
import { Alert, Button, Col, ControlLabel, FormControl, Label, Panel, Radio, Row } from 'react-bootstrap';
import { FormGroupWrapper, ViewWrapper } from './styles';
import { IRecipient, IRecipientSearchReportLookupData } from '@nkr/models/index';
import { MatchStatus, PatientType } from '@nkr/enums/index';
import MaskedInput from 'react-maskedinput';
import AllowDuplicateControl from '@nkr/views/allow-duplicate/allow-duplicate-control';
import { EntityType } from '@nkr/enums/entity-type';

interface IValidation {
    alias: boolean,
    birthDate: boolean
}

interface IProps {
    data: IRecipient,
    lookupData: IRecipientSearchReportLookupData,
    onDataChange: (data: IRecipient) => void,
    onSave: (data: IRecipient) => void,
}

const View = (props: IProps) => {
    const [validation, setValidation] = useState<IValidation>({ alias: true, birthDate: true });
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (props.data == null) {
            setValidation({ alias: true, birthDate: true });
            setDirty(false);
        }
    }, [props.data]);

    const handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        if (name == 'center') {
            data.centerId = parseInt(value);
        }

        if (name == 'alias') {
            data.alias = value;
        }

        if (name == 'firstName') {
            data.firstName = value;
        }

        if (name == 'lastName') {
            data.lastName = value;
        }

        if (name == 'mrn') {
            data.mrn = value;
        }

        if (name == 'dateRegistered') {
            data.dateRegistered = value;
        }

        if (name == 'firstExportableDate') {
            data.firstExportableDate = value;
        }

        if (name == 'inactiveReason') {
            data.inactiveReason = parseInt(value);
        }

        if (name == 'patientTypeId') {
            data.patientTypeId = parseInt(value);
        }

        if (name == 'birthDate') {
            data.birthDate = value;
        }

        if (name == 'chainNumber') {
            data.chainNumber = parseInt(value);
        }

        if (name == 'clusterNumber') {
            data.clusterNumber = parseInt(value);
        }

        if (name == 'position') {
            data.position = parseInt(value);
        }

        if (name == 'statusId') {
            data.statusId = parseInt(value);
        }

        if (name == 'isSwapSaver') {
            data.isSwapSaver = value == '1';
        }

        if (name == 'isRealTimeSwapFailure') {
            data.isRealTimeSwapFailure = value == '1';
        }

        if (name == 'isActive') {
            data.isActive = value == '1';
        }

        if (name == 'isDeleted') {
            data.isDeleted = value == '1';
        }

        if (name == 'sendComplianceEmails') {
            data.sendComplianceEmails = value == '1';
        }

        if (name == 'bloodType') {
            data.bloodType = value;
        }

        if (name == 'gender') {
            data.gender = value;
        }

        if (name == 'targetedChainEnd') {
            data.targetedChainEnd = value == '1';
        }

        props.onDataChange(data);
    };

    const handleSave = () => {
        let validation = validate();
        let isValid = validation.alias && validation.birthDate;

        setValidation(validation);
        setDirty(true);

        if (isValid) {
            let data = _.cloneDeep(props.data);

            props.onSave(data);
        }
    };

    const validate = (): IValidation => {
        let { alias, birthDate } = props.data;
        let birthDateValid = true;

        birthDateValid = moment(birthDate).isValid();

        let today = moment();
        let age = today.diff(birthDate, 'years');

        if (age < 0 || age > 100) {
            birthDateValid = false;
        }

        return {
            alias: alias != '',
            birthDate: birthDateValid
        };
    };

    if (props.data == null || props.lookupData == null)
        return null;

    const isValid = dirty ? validation.alias && validation.birthDate : true;
    const showSwapSelections = props.data.patientTypeId == PatientType.StandardVoucherHolder || props.data.patientTypeId == PatientType.FamilyVoucherHolder;

    const chainRange = _.range(1, 9999);
    const clusterPositionRange = _.range(1, 31);

    return (
        <>
            <ViewWrapper horizontal>
                <div className='modal-container'>
                    {!isValid && (
                        <Row>
                            <Col xs={12} lg={12}>
                                <Alert bsStyle='danger' bsSize='sm' style={{ marginTop: 5, marginBottom: 5 }}>
                                    <div style={{ marginBottom: 5 }}>The following items are blocking us from saving the recipient data...</div>

                                    <ul>
                                        {!validation.alias && (
                                            <li>Please specify the alias</li>
                                        )}

                                        {!validation.birthDate && (
                                            <li>Please enter a valid birth date; birth date must be between 0 and 100 years ago</li>
                                        )}
                                    </ul>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <Panel>
                        <Panel.Body style={{ padding: '10px 5px' }}>
                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Center:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        componentClass='select'
                                        name='center'
                                        value={props.data.centerId}
                                        onChange={handleFieldChange}
                                    >
                                        {props.lookupData.centerList.map((item, index) =>
                                            <option key={'ctr-' + index} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={dirty && !validation.alias ? 'error' : null}>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Alias:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        name='alias'
                                        value={props.data.alias}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>First Name:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        name='firstName'
                                        value={props.data.firstName}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Last Name:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        name='lastName'
                                        value={props.data.lastName}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>MRN:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        name='mrn'
                                        value={props.data.mrn}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Patient Type:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        componentClass='select'
                                        name='patientTypeId'
                                        value={props.data.patientTypeId}
                                        onChange={handleFieldChange}
                                    >
                                        {props.lookupData.patientTypeList.map((item, index) =>
                                            <option key={'pt-' + index} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Chain/Cluster/Position:</Col>
                                <Col xs={12} lg={7}>
                                    <div style={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
                                        <div style={{ flexBasis: '30%' }}>
                                            <FormControl
                                                name='chainNumber'
                                                value={props.data.chainNumber}
                                                onChange={handleFieldChange}
                                            />
                                        </div>
                                        <div>/</div>
                                        <div style={{ flexBasis: '30%' }}>
                                            <FormControl
                                                componentClass='select'
                                                name='clusterNumber'
                                                value={props.data.clusterNumber}
                                                onChange={handleFieldChange}
                                            >
                                                <option value={0}>0</option>
                                                {clusterPositionRange.map(chain =>
                                                    <option key={'ch-' + chain} value={chain}>{chain}</option>
                                                )}
                                            </FormControl>
                                        </div>
                                        <div>/</div>
                                        <div style={{ flexBasis: '30%' }}>
                                            <FormControl
                                                componentClass='select'
                                                name='position'
                                                value={props.data.position}
                                                onChange={handleFieldChange}
                                            >
                                                <option value={0}>0</option>
                                                {clusterPositionRange.map(chain =>
                                                    <option key={'ch-' + chain} value={chain}>{chain}</option>
                                                )}
                                            </FormControl>
                                        </div>
                                    </div>
                                </Col>
                            </FormGroupWrapper>

                            {(props.data.patientTypeId == PatientType.StandardVoucherHolder || props.data.patientTypeId == PatientType.FamilyVoucherHolder) && (
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Is Swap Saver?</Col>
                                    <Col xs={12} lg={7}>
                                        <Radio inline id='isSwapSaverYes' name='isSwapSaver' disabled={!showSwapSelections} value={1} checked={props.data.isSwapSaver} onChange={handleFieldChange}>Yes</Radio>
                                        <Radio inline id='isSwapSaverNo' name='isSwapSaver' disabled={!showSwapSelections} value={0} checked={!props.data.isSwapSaver} onChange={handleFieldChange}>No</Radio>
                                    </Col>
                                </FormGroupWrapper>
                            )}

                            {(props.data.patientTypeId == PatientType.StandardVoucherHolder || props.data.patientTypeId == PatientType.FamilyVoucherHolder) && (
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Is Real Time Swap Failure?</Col>
                                    <Col xs={12} lg={7}>
                                        <Radio inline id='isRealTimeSwapFailureYes' name='isRealTimeSwapFailure' disabled={!showSwapSelections} value={1} checked={props.data.isRealTimeSwapFailure} onChange={handleFieldChange}>Yes</Radio>
                                        <Radio inline id='isRealTimeSwapFailureNo' name='isRealTimeSwapFailure' disabled={!showSwapSelections} value={0} checked={!props.data.isRealTimeSwapFailure} onChange={handleFieldChange}>No</Radio>
                                    </Col>
                                </FormGroupWrapper>
                            )}

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Status:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        componentClass='select'
                                        name='statusId'
                                        value={props.data.statusId}
                                        onChange={handleFieldChange}
                                    >
                                        {props.lookupData.matchStatusList.map((item, index) =>
                                            <option key={'sl-' + index} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Registration Date:</Col>
                                <Col xs={12} lg={7}>
                                    <div className='form-control-static'>{props.data.dateRegistered}</div>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>First Exportable Date:</Col>
                                <Col xs={12} lg={7}>
                                    <div className='form-control-static'>{props.data.firstExportableDate != '' ? props.data.firstExportableDate : '--'}</div>
                                </Col>
                            </FormGroupWrapper>

                            {props.data.statusId == MatchStatus.Transplanted && (
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Transplant Date:</Col>
                                    <Col xs={12} lg={7}>
                                        <div className='form-control-static'>{props.data.transplantDate}</div>
                                    </Col>
                                </FormGroupWrapper>
                            )}

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Blood Type:</Col>
                                <Col xs={12} lg={7}>
                                    <div className='form-control-static'>{props.data.bloodType}</div>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Patient is Exportable?</Col>
                                <Col xs={12} lg={7}>
                                    <div className='form-control-static'>{props.data.isExportable ? <Label bsStyle="success">Yes</Label> : <Label bsStyle="danger">No</Label>}</div>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Patient is Active?</Col>
                                <Col xs={12} lg={7}>
                                    <Radio inline id='isActiveYes' name='isActive' value={1} checked={props.data.isActive} onChange={handleFieldChange}>Yes</Radio>
                                    <Radio inline id='isActiveNo' name='isActive' value={0} checked={!props.data.isActive} onChange={handleFieldChange}>No</Radio>
                                </Col>
                            </FormGroupWrapper>

                            {!props.data.isActive && (
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Inactive Reasons:</Col>
                                    <Col xs={12} lg={7}>
                                        <FormControl
                                            componentClass='select'
                                            name='inactiveReason'
                                            value={props.data.inactiveReason}
                                            onChange={handleFieldChange}
                                        >
                                            {props.lookupData.recipientInactiveReasonList.map((item, index) =>
                                                <option key={'inact-' + index} value={item.id}>{item.description}</option>
                                            )}
                                        </FormControl>
                                    </Col>
                                </FormGroupWrapper>
                            )}

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Recipient is Deleted?</Col>
                                <Col xs={12} lg={7}>
                                    <Radio inline id='isDeletedYes' name='isDeleted' value={1} checked={props.data.isDeleted} onChange={handleFieldChange}>Yes</Radio>
                                    <Radio inline id='isDeletedNo' name='isDeleted' value={0} checked={!props.data.isDeleted} onChange={handleFieldChange}>No</Radio>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Allow Duplicate:</Col>
                                <Col xs={12} lg={7}>
                                    <AllowDuplicateControl entityType={EntityType.Recipient} profileId={props.data.id} />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Send Compliance E-mails?</Col>
                                <Col xs={12} lg={7}>
                                    <Radio inline id='sendComplianceEmailsYes' name='sendComplianceEmails' value={1} checked={props.data.sendComplianceEmails} onChange={handleFieldChange}>Yes</Radio>
                                    <Radio inline id='sendComplianceEmailsNo' name='sendComplianceEmails' value={0} checked={!props.data.sendComplianceEmails} onChange={handleFieldChange}>No</Radio>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Gender:</Col>
                                <Col xs={12} lg={7} style={{ paddingBottom: 7 }}>
                                    <Radio inline id='genderM' name='gender' value={'M'} checked={props.data.gender == 'M'} onChange={handleFieldChange}>Male</Radio>
                                    <Radio inline id='genderF' name='gender' value={'F'} checked={props.data.gender == 'F'} onChange={handleFieldChange}>Female</Radio>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={dirty && !validation.birthDate ? "error" : null}>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Birth Date:</Col>
                                <Col xs={12} lg={7}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ marginRight: 5 }}>
                                            <MaskedInput
                                                name='birthDate'
                                                mask='11/11/1111'
                                                placeholder='mm/dd/yyyy'
                                                className='form-control'
                                                value={props.data.birthDate}
                                                onChange={handleFieldChange} />
                                        </div>
                                    </div>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Targeted Chain End?</Col>
                                <Col xs={12} lg={7}>
                                    <Radio inline id='targetedChainEndYes' name='targetedChainEnd' value={1} checked={props.data.targetedChainEnd} onChange={handleFieldChange}>Yes</Radio>
                                    <Radio inline id='targetedChainEndNo' name='targetedChainEnd' value={0} checked={!props.data.targetedChainEnd} onChange={handleFieldChange}>No</Radio>
                                </Col>
                            </FormGroupWrapper>
                        </Panel.Body>
                    </Panel>

                    <div style={{ textAlign: 'center' }}>
                        <Button bsStyle='primary' bsSize='large' onClick={handleSave}>Save Changes</Button>
                    </div>

                    <div className='row' style={{ marginTop: 0, marginBottom: 10, height: 10 }}></div>
                </div>
            </ViewWrapper>
        </>
    );
}

export default View;